import { useState, useMemo, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { TaskStatus, TaskType, type TaskExtended, type TaskCreateInput } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AddIcon, SpinnerIcon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { SubtaskDraftRow, type SubtaskDraft } from "@/components/tasks/SubtaskDraftRow";
import { GET_PROJECT_ROLES } from "@/hooks/useProjects";
import { ResponsiblePeoplePicker } from "@/components/tasks/ResponsiblePeoplePicker";
import { DeadlineField } from "@/components/tasks/DeadlineField";
import { RecurrenceFields } from "@/components/tasks/RecurrenceFields";
import { CREATE_RECURRING_TASK_SERIES } from "@/graphql/mutations";
import {
  emptyRecurrence,
  occurrenceDates,
  todayYmd,
  MAX_OCCURRENCES,
  type RecurrenceFormState,
} from "@/lib/recurrence";
import type { Project, Task, User } from "@/types";
import type { Plot } from "@/hooks/usePlots";
import type { ProcurementPackage } from "@/hooks/useProcurementPackages";

export function CreateTaskDialog({
  open,
  onOpenChange,
  projects,
  plots,
  procurementPackages,
  users,
  currentUserId,
  createTask: createTaskMutation,
  onTaskCreated,
  onTasksRefresh,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projects: Project[];
  plots: Plot[];
  procurementPackages: ProcurementPackage[];
  users: User[];
  currentUserId: string;
  createTask: (input: TaskCreateInput) => Promise<{ success: boolean; message: string; task?: Task } | undefined>;
  onTaskCreated?: (task: TaskExtended) => void;
  onTasksRefresh?: () => void;
}) {

  const [formData, setFormData] = useState({
    title: "",
    projectId: "",
    plotId: "",
    procurementPackageId: "",
    responsibleUserId: "",
    dueDate: "",
  });
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Recurrence. When enabled the whole submit switches to a single
  // createRecurringTaskSeries call, which writes the series, every task and
  // every cloned alitehtava server-side.
  const [recurrence, setRecurrence] = useState<RecurrenceFormState>(emptyRecurrence());
  const [createSeriesMutation] = useMutation(CREATE_RECURRING_TASK_SERIES);

  // Subtasks drafted before the parent exists. Nothing is sent until submit.
  const [subtaskDrafts, setSubtaskDrafts] = useState<SubtaskDraft[]>([]);
  const [subtaskErrors, setSubtaskErrors] = useState<Record<string, string>>({});
  // Set once the parent task has been created. Its presence means the dialog is
  // in retry mode: the parent is committed and must not be created again, so
  // every retry targets it.
  const [createdParent, setCreatedParent] = useState<Task | null>(null);
  // Drives the repair *layout* only — deliberately separate from
  // `createdParent`, which is set as soon as the parent commits so a retry can
  // never create a second one. On the happy path the parent commits and the
  // dialog then closes, so binding the layout to `createdParent` collapsed the
  // form into the repair summary and flashed it through Radix's 200ms exit
  // animation. Only an actual subtask failure should change what is on screen.
  const [repairMode, setRepairMode] = useState(false);
  // Guards against announcing the same created parent twice (e.g. successful
  // close followed by a stray onOpenChange from Radix).
  const announcedRef = useRef(false);
  const draftKeySeq = useRef(0);

  const failedSubtaskCount = Object.keys(subtaskErrors).length;

  // A recurring create needs an end date, and a rule that produces at least one
  // task without blowing the server's cap.
  const recurrenceIsSubmittable = useMemo(() => {
    if (!recurrence.enabled) return true;
    if (!recurrence.endDate) return false;
    const count = occurrenceDates(
      recurrence,
      formData.dueDate || todayYmd(),
    ).length;
    return count > 0 && count <= MAX_OCCURRENCES;
  }, [recurrence, formData.dueDate]);

  const addSubtaskDraft = () => {
    draftKeySeq.current += 1;
    setSubtaskDrafts((prev) => [
      ...prev,
      { key: `draft-${draftKeySeq.current}`, title: "", description: "" },
    ]);
  };

  // Guards the one-shot project auto-fill (#1) so a late-arriving role query
  // never clobbers a project the user has since chosen by hand.
  const didAutofillProject = useRef(false);

  // Reset the form each time the dialog opens. The `open` prop is flipped by
  // the parent (there is no DialogTrigger), so Dialog.onOpenChange never fires
  // on open — keying the reset on `open` itself is the reliable signal.
  // Declared before the auto-fill effects so its blank state is queued first.
  useEffect(() => {
    didAutofillProject.current = false;
    if (!open) return;
    setFormData({
      title: "",
      projectId: "",
      plotId: "",
      procurementPackageId: "",
      responsibleUserId: "",
      dueDate: "",
    });
    setError(null);
    // Clearing `createdParent` here is what makes a reopened dialog create a
    // fresh parent rather than resuming the previous one.
    setSubtaskDrafts([]);
    setSubtaskErrors({});
    setRecurrence(emptyRecurrence());
    setCreatedParent(null);
    setRepairMode(false);
    announcedRef.current = false;
  }, [open]);

  const filteredPlots = useMemo(() => {
    if (!formData.projectId) return [];
    return plots.filter((plot) => plot.projectId === formData.projectId);
  }, [plots, formData.projectId]);

  const filteredProcurementPackages = useMemo(() => {
    if (!formData.plotId) return [];
    return procurementPackages.filter((pp) => pp.plotId === formData.plotId);
  }, [procurementPackages, formData.plotId]);

  // #1 — the opening user's project roles, used to pre-fill the project with
  // the one where their most recently created role lives.
  const { data: myRolesData, loading: myRolesLoading } = useQuery(GET_PROJECT_ROLES, {
    variables: { filter: { userId: currentUserId }, limit: 500 },
    skip: !open || !currentUserId,
    fetchPolicy: "cache-and-network",
  });

  // #3 — every person holding a role in the selected project.
  const { data: projectRolesData, loading: projectRolesLoading } = useQuery(GET_PROJECT_ROLES, {
    variables: { filter: { projectId: formData.projectId }, limit: 500 },
    skip: !open || !formData.projectId,
    fetchPolicy: "cache-and-network",
  });

  const projectPeople = useMemo<User[]>(() => {
    const items = (projectRolesData?.projectRoles?.items || []) as Array<{ userId: string }>;
    const usersById = new Map(users.map((u) => [u.id, u]));
    const seen = new Set<string>();
    const result: User[] = [];
    for (const role of items) {
      if (seen.has(role.userId)) continue;
      seen.add(role.userId);
      const user = usersById.get(role.userId);
      if (user && user.isActive) result.push(user);
    }
    return result;
  }, [projectRolesData, users]);

  // #1 — once the role query settles, pick the project of the latest-created
  // role (if it exists in the loaded projects) and pre-fill it. Runs at most
  // once per dialog open; manual selection is never overwritten.
  useEffect(() => {
    if (!open || myRolesLoading || didAutofillProject.current) return;
    didAutofillProject.current = true;
    const roles = (myRolesData?.projectRoles?.items || []) as Array<{
      projectId: string;
      createdAt?: string | null;
    }>;
    if (roles.length === 0) return;
    const latest = roles.reduce((best, role) =>
      (role.createdAt || "") > (best.createdAt || "") ? role : best,
    );
    if (!projects.some((p) => p.id === latest.projectId)) return;
    setFormData((prev) =>
      prev.projectId
        ? prev
        : { ...prev, projectId: latest.projectId, plotId: "", procurementPackageId: "", responsibleUserId: "" },
    );
  }, [open, myRolesLoading, myRolesData, projects]);

  // #2 — when a project has exactly one plot, auto-select it (covers both the
  // auto-filled project and a manual project change). Reads `prev`/`plots`
  // inside the updater so it never acts on a stale pre-reset project.
  useEffect(() => {
    setFormData((prev) => {
      if (!prev.projectId) return prev;
      const plotsForProject = plots.filter((p) => p.projectId === prev.projectId);
      if (plotsForProject.length !== 1) return prev;
      if (prev.plotId === plotsForProject[0].id) return prev;
      return { ...prev, plotId: plotsForProject[0].id, procurementPackageId: "" };
    });
  }, [formData.projectId, plots]);

  /** Notify the parent page about a created task, filling in the responsible
   *  user from the already-loaded list when the mutation didn't return it. */
  const announceCreated = (task: Task) => {
    if (onTaskCreated) {
      const newTask = { ...task } as TaskExtended;
      if (newTask.responsibleUserId && !newTask.responsibleUser?.firstName) {
        const responsible = users.find((u) => u.id === newTask.responsibleUserId);
        if (responsible) {
          newTask.responsibleUser = responsible;
        }
      }
      onTaskCreated(newTask);
    }
    if (onTasksRefresh) {
      onTasksRefresh();
    }
  };

  /**
   * Single exit point for the dialog.
   *
   * Any close after the parent was created announces it — including Sulje,
   * Escape and an overlay click during subtask repair. The parent exists in the
   * database either way, so the board must learn about it however the user
   * leaves.
   */
  const handleOpen = (isOpen: boolean) => {
    if (!isOpen && createdParent && !announcedRef.current) {
      announcedRef.current = true;
      announceCreated(createdParent);
    }
    onOpenChange(isOpen);
  };

  /**
   * Create the drafted subtasks under `parentId`, sequentially.
   *
   * Sequential rather than parallel: `createTask` derives the parent's status
   * from its children server-side, so concurrent inserts would contend on the
   * parent's optimistic-locking version for no gain at these list sizes.
   *
   * Returns the drafts that failed, each with its message. Successful drafts are
   * dropped from state as they land, so a retry never re-creates them.
   */
  const createSubtasks = async (
    parentId: string,
    drafts: SubtaskDraft[],
  ): Promise<Record<string, string>> => {
    const failures: Record<string, string> = {};
    const succeeded = new Set<string>();

    for (const draft of drafts) {
      const title = draft.title.trim();
      if (!title) {
        // Blank rows are treated as "not filled in", not as errors.
        succeeded.add(draft.key);
        continue;
      }
      try {
        const result = await createTaskMutation({
          title,
          description: draft.description.trim() || undefined,
          status: TaskStatus.PENDING,
          taskType: TaskType.INTERNAL_SUBTASK,
          parentId,
          createdByUserId: currentUserId || undefined,
        });
        if (result?.success) {
          succeeded.add(draft.key);
        } else {
          failures[draft.key] =
            result?.message || "Alitehtävän luonti epäonnistui.";
        }
      } catch (err) {
        console.error("Failed to create subtask:", err);
        failures[draft.key] =
          err instanceof Error
            ? err.message
            : "Alitehtävän luonti epäonnistui. Yritä uudelleen.";
      }
    }

    setSubtaskDrafts((prev) => prev.filter((d) => !succeeded.has(d.key)));
    return failures;
  };

  /**
   * Retry path — the parent is already committed, so only the remaining subtask
   * drafts are attempted. Keying off `createdParent` is what stops a retry
   * creating a second parent task. Deliberately independent of the main-form
   * validity guard below: those fields are no longer editable at this point.
   */
  const handleRetrySubtasks = async () => {
    if (!createdParent) return;
    setIsCreating(true);
    setError(null);
    setSubtaskErrors({});
    try {
      const failures = await createSubtasks(createdParent.id, subtaskDrafts);
      setSubtaskErrors(failures);
      if (Object.keys(failures).length === 0) {
        handleOpen(false);
      }
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Recurring create.
   *
   * One mutation writes the series, every task and every cloned alitehtävä, so
   * unlike the single-task path there is no partial state to repair: the
   * per-subtask client loop would be one round trip per alitehtävä per task.
   */
  const handleCreateSeries = async () => {
    setIsCreating(true);
    setError(null);
    try {
      const hasProcurementPackage = !!formData.procurementPackageId;
      const result = await createSeriesMutation({
        variables: {
          input: {
            title: formData.title,
            frequency: recurrence.frequency,
            weekday:
              recurrence.frequency === "DAILY_WEEKDAYS" ? null : recurrence.weekday,
            weekOfMonth:
              recurrence.frequency === "MONTHLY_NTH_WEEKDAY"
                ? recurrence.weekOfMonth
                : null,
            startDate: formData.dueDate || null,
            endDate: recurrence.endDate,
            plotId: hasProcurementPackage ? null : formData.plotId,
            procurementPackageId: hasProcurementPackage
              ? formData.procurementPackageId
              : null,
            responsibleUserId: formData.responsibleUserId,
            createdByUserId: currentUserId || null,
            subtasks: subtaskDrafts
              .filter((d) => d.title.trim())
              .map((d) => ({
                title: d.title.trim(),
                description: d.description.trim() || null,
              })),
          },
        },
      });

      const payload = result.data?.createRecurringTaskSeries;
      if (!payload?.success || !payload.firstTask) {
        setError(payload?.message || "Toistuvan tehtävän luonti epäonnistui.");
        return;
      }

      announcedRef.current = true;
      onOpenChange(false);
      announceCreated(payload.firstTask as Task);
    } catch (err) {
      console.error("Failed to create recurring task series:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Toistuvan tehtävän luonti epäonnistui. Yritä uudelleen.",
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreate = async () => {
    // `createdParent` — not `repairMode` — is what says the parent is committed.
    // The footer now switches on `repairMode`, so during the close animation of
    // a successful create this button still reads "Luo tehtävä"; without this
    // guard a click in that window would create a duplicate parent.
    if (createdParent) return;
    if (!formData.title || !formData.projectId || !formData.plotId || !formData.responsibleUserId) {
      return;
    }

    if (recurrence.enabled) {
      await handleCreateSeries();
      return;
    }

    setIsCreating(true);
    setError(null);
    setSubtaskErrors({});
    try {
      const hasProcurementPackage = !!formData.procurementPackageId;
      const input: TaskCreateInput = {
        title: formData.title,
        status: TaskStatus.PENDING,
        taskType: TaskType.INTERNAL_MAIN_TASK,
        plotId: hasProcurementPackage ? undefined : formData.plotId,
        procurementPackageId: hasProcurementPackage ? formData.procurementPackageId : undefined,
        responsibleUserId: formData.responsibleUserId,
        dueDate: formData.dueDate || undefined,
        createdByUserId: currentUserId || undefined,
      };

      const result = await createTaskMutation(input);

      if (!result?.success || !result?.task) {
        setError(result?.message || "Tehtävän luonti epäonnistui.");
        return;
      }

      // The parent is committed from here on. Record it before touching
      // subtasks so any failure below becomes a retry rather than a re-create.
      const parentTask = result.task;
      setCreatedParent(parentTask);

      const failures = await createSubtasks(parentTask.id, subtaskDrafts);

      if (Object.keys(failures).length > 0) {
        // Parent created, some subtasks did not. Stay open as a retry surface;
        // the parent is announced when the dialog finally closes, so the board
        // does not update mid-repair. This is the only place the layout is
        // allowed to switch — see `repairMode`.
        setSubtaskErrors(failures);
        setRepairMode(true);
        return;
      }

      announcedRef.current = true;
      onOpenChange(false);
      announceCreated(parentTask);
    } catch (err) {
      console.error("Failed to create task:", err);
      setError(
        err instanceof Error ? err.message : "Tehtävän luonti epäonnistui. Yritä uudelleen."
      );
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="w-screen h-dvh max-w-full max-h-dvh rounded-none flex flex-col overflow-hidden box-border md:w-full md:h-auto md:max-w-[550px] md:max-h-[90dvh] md:rounded-lg">
        <DialogHeader className="text-left pr-8 min-w-0">
          <DialogTitle>Luo uusi tehtävä</DialogTitle>
        </DialogHeader>

        {/* Body — `touch-pan-y` tells the browser only vertical touch
            gestures may scroll this region, killing the horizontal-swipe
            wiggle that mobile Safari produces when an input's caret
            reaches its edge. `overscroll-contain` prevents the scroll
            from bubbling out to the underlying page. */}
        <div className="grid gap-4 py-4 px-1 overflow-y-auto overflow-x-hidden min-h-0 min-w-0 touch-pan-y overscroll-contain">
          {repairMode ? (
            // Repair mode: the parent is already committed, so its fields are
            // shown as a read-only summary rather than editable inputs that
            // would silently do nothing.
            <div className="grid gap-1 min-w-0">
              <Label>Tehtävä</Label>
              <p className="text-sm font-medium break-words">
                {createdParent?.title}
              </p>
              <p className="text-xs text-muted-foreground">
                Tehtävä on jo luotu. Täydennä puuttuvat alitehtävät alla.
              </p>
            </div>
          ) : (
            <div className="grid gap-2.5 min-w-0">
              <Label htmlFor="task-title">
                Tehtävän nimi <span className="text-red-500">*</span>
              </Label>
              <Input
                id="task-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Syötä tehtävän nimi"
                className="w-full max-w-full box-border"
              />
            </div>
          )}

          {/* Subtasks — drafted here, created after the parent exists. */}
          <div className="grid gap-2 min-w-0">
            <Label>Alitehtävät</Label>
            {subtaskDrafts.length > 0 && (
              <div className="space-y-2 min-w-0">
                {subtaskDrafts.map((draft, index) => (
                  <SubtaskDraftRow
                    key={draft.key}
                    value={draft}
                    index={index}
                    disabled={isCreating}
                    error={subtaskErrors[draft.key] ?? null}
                    onChange={(next) =>
                      setSubtaskDrafts((prev) =>
                        prev.map((d) => (d.key === next.key ? next : d)),
                      )
                    }
                    onRemove={() =>
                      setSubtaskDrafts((prev) =>
                        prev.filter((d) => d.key !== draft.key),
                      )
                    }
                  />
                ))}
              </div>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addSubtaskDraft}
              disabled={isCreating}
              className="w-full justify-start text-muted-foreground hover:text-foreground motion-safe:transition-colors"
            >
              <AddIcon className="h-4 w-4 mr-1" />
              Lisää alitehtävä
            </Button>
          </div>

          {!repairMode && (
          <>
          <div className="grid grid-cols-2 gap-4 min-w-0">
            <div className="grid gap-2 min-w-0">
              <Label>
                Hanke <span className="text-red-500">*</span>
              </Label>
              <Combobox
                options={projects.map((project) => ({ value: project.id, label: project.name }))}
                value={formData.projectId || null}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    projectId: value || "",
                    plotId: "",
                    procurementPackageId: "",
                    responsibleUserId: "",
                  })
                }
                placeholder="Valitse hanke"
                searchPlaceholder="Hae hanketta..."
                disabled={repairMode}
                className="w-full min-w-0"
              />
            </div>

            <div className="grid gap-2 min-w-0">
              <Label>
                Tontti <span className="text-red-500">*</span>
              </Label>
              <Combobox
                options={filteredPlots.map((plot) => ({ value: plot.id, label: plot.name }))}
                value={formData.plotId || null}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    plotId: value || "",
                    procurementPackageId: "",
                  })
                }
                placeholder={formData.projectId ? "Valitse tontti" : "Valitse ensin hanke"}
                searchPlaceholder="Hae tonttia..."
                disabled={!formData.projectId || repairMode}
                className="w-full min-w-0"
              />
            </div>
            </div>

          <div className="grid gap-2 min-w-0">
            <Label>Hankintapaketti</Label>
            <Combobox
              options={filteredProcurementPackages.map((pp) => ({
                value: pp.id,
                label: `${pp.code} ${pp.name}`,
              }))}
              value={formData.procurementPackageId || null}
              onChange={(value) =>
                setFormData({
                  ...formData,
                  procurementPackageId: value || "",
                })
              }
              placeholder={
                formData.plotId
                  ? "Valitse hankintapaketti (valinnainen)"
                  : "Valitse ensin tontti"
              }
              searchPlaceholder="Hae hankintapakettia..."
              disabled={!formData.plotId || repairMode}
              className="w-full min-w-0"
            />
          </div>

          <div className="grid gap-2 min-w-0">
            <Label>
              Vastuuhenkilö <span className="text-red-500">*</span>
            </Label>
            <ResponsiblePeoplePicker
              people={projectPeople}
              value={formData.responsibleUserId}
              onChange={(userId) => setFormData({ ...formData, responsibleUserId: userId })}
              emptyMessage={
                !formData.projectId
                  ? "Valitse ensin hanke."
                  : projectRolesLoading
                    ? "Ladataan vastuuhenkilöitä…"
                    : 'Tälle projektille ei ole luotu ainuttakaan projekti roolia. Luo rooli "Hankkeet" sivun projektinmuokkaus ikkunassa.'
              }
            />
          </div>

          {/* Toistuvuus lives inside the deadline block rather than beside it:
              switching it on is what turns määräaika into the sarja's first
              task, and Sarjan asetukset then expands underneath the calendar. */}
          <div className="grid gap-2 min-w-0">
            <div className="flex items-center gap-3">
              <Label>
                {recurrence.enabled ? "Ensimmäisen tehtävän määräaika" : "Määräaika"}
              </Label>
              <button
                type="button"
                onClick={() =>
                  setRecurrence({ ...recurrence, enabled: !recurrence.enabled })
                }
                disabled={isCreating}
                className={cn(
                  "rounded-full border px-3 py-0.5 text-xs transition-colors",
                  recurrence.enabled
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input bg-background hover:bg-accent hover:text-accent-foreground",
                )}
              >
                Toistuva
              </button>
            </div>
            <DeadlineField
              value={formData.dueDate}
              onChange={(dueDate) => setFormData({ ...formData, dueDate })}
            />

            {recurrence.enabled && (
              <RecurrenceFields
                value={recurrence}
                onChange={setRecurrence}
                startYmd={formData.dueDate || todayYmd()}
                disabled={isCreating}
              />
            )}
          </div>
          </>
          )}
        </div>

        {failedSubtaskCount > 0 && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            Tehtävä luotiin, mutta {failedSubtaskCount} alitehtävän luonti
            epäonnistui.
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpen(false)}
            disabled={isCreating}
          >
            {repairMode ? "Sulje" : "Peruuta"}
          </Button>
          <Button
            onClick={repairMode ? handleRetrySubtasks : handleCreate}
            disabled={
              isCreating ||
              (repairMode
                ? subtaskDrafts.length === 0
                : !formData.title ||
                  !formData.projectId ||
                  !formData.plotId ||
                  !formData.responsibleUserId ||
                  (recurrence.enabled && !recurrenceIsSubmittable))
            }
          >
            {isCreating && <SpinnerIcon className="mr-2 h-4 w-4 animate-spin" />}
            {isCreating
              ? "Luodaan..."
              : repairMode
                ? "Yritä uudelleen"
                : "Luo tehtävä"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
